import { zodResolver } from "@hookform/resolvers/zod";
import Close from "@mui/icons-material/Close";
import Save from "@mui/icons-material/Save";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid2 from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import classNames from "classnames";
import _ from "lodash";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { DocumentAttrs, ProcessingAttrs } from "../../../interfaces/orion";
import PDFViewer from "../../Common/PDFViewer";
import { useDocument, useUpdateReview } from "../../Orion/OrionQueries";
import ClassificationForm from "../../Orion/Review/ClassificationForm";
import ExtractionForm from "../../Orion/Review/ExtractionForm";
import {
  fillMissingExtraction,
  filterClassificationQueries,
  filterExtractionQueries,
  getCompleteOutput,
  getResultFormSchema,
} from "../../Orion/Review/ResultFormUtils";
import { DialogProps } from "../useDialogs";

export interface EditExtractionDialogProps {
  processing: ProcessingAttrs;
  coreIdx: number;
  document: DocumentAttrs;
}

const useReviewForm = ({
  processing,
  coreIdx,
}: {
  processing: ProcessingAttrs;
  coreIdx: number;
}) => {
  const config = processing.config;
  const result = processing.result!.analysis[coreIdx];
  const review = processing.review!.analysis[coreIdx];
  const schema = useMemo(() => getResultFormSchema(config), [config]);
  type ReviewForm = z.infer<typeof schema>;
  const filledOutput = useMemo(() => fillMissingExtraction(result), [result]);
  const filledReviewedOutput = fillMissingExtraction(review);

  const defaultValues = getCompleteOutput(
    processing.config,
    filledOutput,
    filledReviewedOutput
  );

  return useForm<ReviewForm>({
    defaultValues: defaultValues,
    resolver: zodResolver(schema),
  });
};

const EditExtractionDialog = ({
  payload,
  open,
  onClose,
}: DialogProps<EditExtractionDialogProps>) => {
  const { processing, coreIdx } = payload;
  const { ...methods } = useReviewForm({
    processing,
    coreIdx,
  });
  const { data: documentUrl } = useDocument(payload.document._id);

  const { mutate: updateReview, isPending } = useUpdateReview();

  const onSubmit = (data: any) => {
    console.log("Form values at submit:", data);

    const filteredClassificationQueries = processing.config.classification
      ? filterClassificationQueries(
          processing.config.classification.queries,
          data.classification.split
        ).map((query) => query.name)
      : [];
    const filteredExtractionQueries = processing.config.extraction
      ? filterExtractionQueries(
          processing.config.extraction.queries,
          data.classification
        ).map((query) => query.id)
      : [];
    const payload = {
      classification: data.classification
        ? _.pick(data.classification, filteredClassificationQueries)
        : null,
      extraction: data.extraction
        ? _.pick(data.extraction, filteredExtractionQueries)
        : null,
    };
    console.log("Payload", payload);
    updateReview({
      processingId: processing._id,
      review: payload,
      coreIndex: coreIdx,
    });
    onClose();
  };

  const onError = (data: any) => {
    console.log("Errors", data);
    console.log("Errored Form", methods.watch());
  };
  const isDirty = _.keys(methods.formState.dirtyFields).length > 0;

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        onClose={() => onClose()}
        slotProps={{
          paper: {
            component: "form",
            onSubmit: methods.handleSubmit(onSubmit, onError),
            sx: {
              height: "90vh",
            },
          },
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Modifier les données extraites
          <IconButton
            aria-label="close"
            onClick={() => onClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        {isDirty && (
          <Alert severity="info" sx={{ mx: 3, my: 1 }}>
            Vous avez des changements non enregistrés
          </Alert>
        )}
        <DialogContent
          dividers
          sx={{ pt: isDirty ? 1 : undefined, overflow: "hidden" }}
        >
          <Grid2
            container
            spacing={2}
            sx={{ height: "100%", overflow: "hidden", display: "flex" }}
          >
            <Grid2 size={6} sx={{ height: "100%", overflow: "hidden" }}>
              <PDFViewer
                title={payload.document.name ?? ""}
                fileUrl={documentUrl?.url}
              />
            </Grid2>
            <Grid2 size={6} sx={{ height: "100%", overflow: "auto" }}>
              <div
                className={classNames("flex flex-col flex-1 p-4 gap-2", {
                  "pointer-events-none opacity-50": isPending,
                })}
              >
                {(!!processing.config.classification ||
                  !!processing.config.split) && (
                  <ClassificationForm config={processing.config} />
                )}
                <ExtractionForm config={processing.config} />
              </div>
            </Grid2>
          </Grid2>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()}>Annuler</Button>
          <Button
            type="submit"
            disabled={!isDirty}
            aria-label="Save"
            color="primary"
            variant="contained"
            startIcon={<Save />}
          >
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default EditExtractionDialog;
